/* ============================================================================
   GHOST LOADERS — React (TypeScript). Pairs with ghost.css.
   For: EmissionsPlatform / Metrifer web, PlantEmissionsTool, the 4sum admin
   console, and any new React surface.

   THE RULE: a screen waiting on data shows the SHAPE of that data. No spinner,
   no "Loading" string. Build the ghost from the SAME layout components as the
   real thing wherever you can, so the two cannot drift apart.

       if (!rows) return <GhostGroup label="Loading facilities"><GhostRows n={6} trailing /></GhostGroup>;

   There is a JavaScript twin at ./Ghost.jsx for the .jsx codebases (ClubTime,
   PJA Compliance) — keep the two in step.
   ============================================================================ */

import React from 'react';

type Sizeable = { className?: string; style?: React.CSSProperties };

const hidden = { 'aria-hidden': true } as const;

/** One line standing in for text. Sized in em, so it inherits the font-size
 *  of whatever it is nested in — put it inside the <h2> and it is heading-sized. */
export function GhostText({ w, className = '', style }: Sizeable & { w?: string }) {
  return (
    <span
      {...hidden}
      className={`ghost ghost-text ${className}`}
      style={{ ...(w ? ({ ['--ghost-w' as string]: w } as React.CSSProperties) : null), ...style }}
    />
  );
}

export function GhostBlock({ w = '100%', h = '64px', className = '', style }: Sizeable & { w?: string; h?: string }) {
  return (
    <div
      {...hidden}
      className={`ghost ghost-block ${className}`}
      style={{ ['--ghost-w' as string]: w, ['--ghost-h' as string]: h, ...style } as React.CSSProperties}
    />
  );
}

export function GhostCircle({ d = '32px', className = '', style }: Sizeable & { d?: string }) {
  return (
    <div
      {...hidden}
      className={`ghost ghost-circle ${className}`}
      style={{ ['--ghost-d' as string]: d, ...style } as React.CSSProperties}
    />
  );
}

export function GhostPill({ w = '72px', className = '' }: Sizeable & { w?: string }) {
  return <span {...hidden} className={`ghost ghost-pill ${className}`} style={{ ['--ghost-w' as string]: w } as React.CSSProperties} />;
}

/** A number that will land in a stat tile. Narrower than the tile on purpose,
 *  so the real figure never looks like it shrank when it arrives. */
export function GhostFigure({ chars = 4.5, className = '' }: Sizeable & { chars?: number }) {
  return <span {...hidden} className={`ghost ghost-figure ${className}`} style={{ ['--ghost-w' as string]: `${chars}ch` } as React.CSSProperties} />;
}

/** Prose. Ragged right edge — an even block reads as a table, not a paragraph. */
export function GhostPara({ lines = 3, className = '' }: Sizeable & { lines?: number }) {
  return (
    <div {...hidden} className={`ghost-para ${className}`}>
      {Array.from({ length: lines }, (_, i) => <GhostText key={i} />)}
    </div>
  );
}

/** The workhorse: n list rows. Queues, feeds, rosters, registries, boards. */
export function GhostRows({
  n = 5,
  avatar = true,
  subtitle = true,
  trailing = false,
  rowClassName = '',
}: {
  n?: number;
  avatar?: boolean | string;
  subtitle?: boolean;
  trailing?: boolean | number;
  rowClassName?: string;
}) {
  return (
    <>
      {Array.from({ length: n }, (_, i) => (
        <div {...hidden} key={i} className={`ghost-row ${rowClassName}`}>
          {avatar ? <GhostCircle d={typeof avatar === 'string' ? avatar : '28px'} /> : null}
          <div className="ghost-para" style={{ flex: 1 }}>
            {/* Deterministic, uneven widths: varied enough to read as content,
                stable enough that a re-render does not make the ghost twitch. */}
            <GhostText w={`${70 + ((i * 7) % 25)}%`} />
            {subtitle ? <GhostText w={`${38 + ((i * 11) % 20)}%`} /> : null}
          </div>
          {trailing ? <GhostFigure chars={typeof trailing === 'number' ? trailing : 5} /> : null}
        </div>
      ))}
    </>
  );
}

/** A table body that keeps the real column widths, so the header never jumps. */
export function GhostTableRows({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr {...hidden} key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}><GhostText w={c === 0 ? '82%' : `${44 + ((r + c) * 9) % 30}%`} /></td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** Label + figure, for a KPI strip. */
export function GhostStat() {
  return (
    <div {...hidden} className="ghost-para">
      <GhostText w="42%" />
      <GhostFigure chars={6} className="ghost-figure--lg" />
    </div>
  );
}

/**
 * Wraps whatever is waiting. Carries aria-busy and the one visually-hidden
 * announcement — the only place the word "Loading" is allowed to live, because
 * it is never painted.
 */
export function GhostGroup({
  label = 'Loading content',
  className = '',
  children,
}: {
  label?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div aria-busy="true" className={`ghost-group ${className}`}>
      <span className="ghost-sr" role="status">{label}</span>
      {children}
    </div>
  );
}

/**
 * Props for a button that is mid-request. It keeps its label — the user needs
 * to still know what they pressed — dims, and stops taking presses. No spinner,
 * no text swap, no reflow, by house rule.
 *
 *     <button {...pending(saving)} onClick={save}>Save</button>
 */
/* `pending` MUST be spread with the element's own base class/style passed in,
   not alongside it. It returns a `className` (RN: `style`) of its own, so
   spreading it after an existing one silently erases that one:

       <button className="btn primary" {...pending(saving)}>   // WRONG - loses "btn primary"
       <button {...pending(saving, 'btn primary')}>            // right

   Found 2026-09-04 converting PJA Compliance, before it shipped anywhere. */
export function pending(isPending: boolean, baseClassName?: string) {
  return {
    'aria-busy': isPending || undefined,
    className: [baseClassName, isPending ? 'is-pending' : null].filter(Boolean).join(' ') || undefined,
    disabled: isPending,
  };
}
